import mixpanel, { type Dict } from 'mixpanel-browser';

const TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const DEBUG = import.meta.env.DEV;

let initialized = false;

export function initMixpanel(): void {
  if (initialized) return;
  if (!TOKEN) return;

  mixpanel.init(TOKEN, {
    debug: DEBUG,
    track_pageview: false,
    persistence: 'localStorage',
    ignore_dnt: false,
    api_host: 'https://api.mixpanel.com',
  });

  initialized = true;
}

// Minimal identify for the earliest authenticated boundary (the app-shell
// route loader). It only pins the distinct_id to the Django user id so
// the first `page_viewed` on a cold load / post-login redirect is
// attributed to the logged-in user instead of the anonymous `$device` id.
// Richer people.set/register enrichment still happens in the AppContent
// effect once the full profile and LaunchDarkly flags are available.
// Skips redundant re-identify (the loader runs on every authenticated
// navigation) so we don't fire a needless $identify/people merge each time.
export function identifyUserId(userId: string): void {
  if (!initialized) return;
  if (mixpanel.get_distinct_id() === userId) return;
  mixpanel.identify(userId);
}

export function identifyUser(args: {
  userId: string;
  email?: string;
  role?: string;
  isLiftaiAdmin?: boolean;
  customerGroupId?: string | null;
  customerGroupName?: string | null;
  customerGroupKind?: 'client' | 'consultant' | null;
  currencyCode?: 'GBP' | 'USD';
  locale?: string;
}): void {
  if (!initialized) return;

  mixpanel.identify(args.userId);
  mixpanel.people.set({
    $email: args.email,
    role: args.role,
    is_liftai_admin: args.isLiftaiAdmin,
    customer_group_id: args.customerGroupId,
    customer_group_name: args.customerGroupName,
    customer_group_kind: args.customerGroupKind,
    currency_code: args.currencyCode,
    locale: args.locale,
  });
  mixpanel.register({
    role: args.role,
    is_liftai_admin: args.isLiftaiAdmin,
    customer_group_id: args.customerGroupId,
    customer_group_kind: args.customerGroupKind,
  });
}

export function setSuperProperties(properties: Dict): void {
  if (!initialized) return;
  mixpanel.register(properties);
}

export function trackEvent(eventName: string, properties?: Dict): void {
  if (!initialized) return;
  mixpanel.track(eventName, properties);
}

export function resetUser(): void {
  if (!initialized) return;
  mixpanel.reset();
}
